"use client";

import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { FrameIcon } from "./booking-icons";

type HeroStep = {
  title: string;
  description: string;
};

export default function BookingHero() {
  const t = useTranslations("Booking");
  const locale = useLocale();
  const isArabic = locale === "ar";
  const steps = t.raw("heroSteps") as HeroStep[];

  return (
    <section
      dir={isArabic ? "rtl" : "ltr"}
      className="animate-from-bottom animate-delay-100 relative px-4 pb-4 md:px-6 lg:px-8"
    >
      <div className="mx-auto grid w-full max-w-310 grid-cols-1 items-center gap-8 lg:grid-cols-[1.32fr_0.68fr] lg:gap-6">
        <div className="flex flex-col items-start gap-8 text-start lg:gap-11">
          <div className="flex flex-col items-start gap-3">
            <p className="m-0 text-[18px] font-semibold leading-[1.8] text-white min-[500px]:text-[24px]">
              {t("heroEyebrow")}
            </p>
            <h1 className="m-0 bg-[linear-gradient(89.18deg,#0CA5F0_15.53%,#FFFFFF_220.84%)] bg-clip-text text-[28px] font-black leading-[1.4] text-transparent min-[500px]:text-[34px] lg:text-[40px]">
              {t("heroTitle")}
            </h1>
          </div>

          {/* The design separates the three promises with hairline rules
              rather than the connector chain used elsewhere on the site. */}
          <ol className="m-0 flex w-full list-none flex-col gap-6 p-0 lg:flex-row lg:items-center lg:gap-0">
            {steps.map((step, index) => (
              <li
                key={step.title}
                className={`flex min-w-0 items-center gap-4 lg:flex-1 ${
                  index < steps.length - 1
                    ? "lg:border-e lg:border-white/12 lg:pe-8"
                    : ""
                } ${index > 0 ? "lg:ps-8" : ""}`}
              >
                <FrameIcon className="size-9 shrink-0" />
                <span className="flex flex-col gap-1">
                  <span className="text-[17px] font-bold leading-[1.5] text-white">
                    {step.title}
                  </span>
                  <span className="text-[14px] leading-[1.7] text-white/56">
                    {step.description}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="relative order-first flex min-h-70 items-center justify-center lg:order-none lg:min-h-95">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <span className="absolute left-1/2 top-1/2 size-70 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(4,209,241,0.22)_0%,rgba(4,209,241,0)_68%)] blur-2xl lg:size-95" />
            <span className="absolute left-1/2 top-1/2 size-64 -translate-x-1/2 -translate-y-1/2 rotate-[18deg] rounded-full border border-white/10 lg:size-88" />
            <span className="absolute left-1/2 top-1/2 size-76 -translate-x-1/2 -translate-y-1/2 -rotate-[14deg] rounded-full border border-dashed border-white/8 lg:size-104" />
            <span className="absolute left-[14%] top-[22%] size-2 rounded-full bg-cyan-500 shadow-[0_0_18px_6px_rgba(4,209,241,0.45)]" />
          </div>

          <Image
            src="/assets/images/sabah/robot-cta.png"
            alt={t("heroImageAlt")}
            width={430}
            height={588}
            priority
            className="relative z-10 h-auto w-52 object-contain min-[500px]:w-60 lg:w-75"
          />
        </div>
      </div>
    </section>
  );
}
